import React, { useState, useEffect, useRef } from 'react';
import { homeApi } from '../../api/api';
import { AdminFieldInput, AdminUrlField } from '../components/AdminUI';

const CARDS = [
  {
    key: 'seva_upi',
    id: 'upi',
    title: 'UPI Payment',
    icon: 'fas fa-mobile-alt',
    iconColor: 'text-orange-500',
    hint: 'Shown on the Donate page UPI card.',
    fields: [
      { key: 'payeeName', label: 'Name', placeholder: 'Shree Ram Ranuja Dham Trust' },
      { key: 'upiNumber', label: 'UPI Number', placeholder: '9876543210' },
      { key: 'upiId', label: 'UPI ID', placeholder: 'shreeramranujadham@upi' },
    ],
    defaults: {
      payeeName: '',
      upiNumber: '',
      upiId: '',
      qrImage: '',
    },
  },
  {
    key: 'seva_bank',
    id: 'bank',
    title: 'Bank Transfer',
    icon: 'fas fa-university',
    iconColor: 'text-orange-500',
    hint: 'Shown on the Donate page bank transfer card.',
    fields: [
      { key: 'accountName', label: 'Account Name', placeholder: 'Shree Ram Ranuja Dham Trust' },
      { key: 'accountNumber', label: 'Account Number', placeholder: '1234567890123456' },
      { key: 'ifsc', label: 'IFSC Code', placeholder: 'SBIN0001234' },
      { key: 'bankName', label: 'Bank Name', placeholder: 'State Bank of India' },
      { key: 'branch', label: 'Branch', placeholder: 'Sacred City Branch' },
    ],
    defaults: {
      accountName: '',
      accountNumber: '',
      ifsc: '',
      bankName: '',
      branch: '',
    },
  },
];

function normalizeCard(raw, card) {
  const d = raw && typeof raw === 'object' ? raw : {};
  const next = {};
  const keys = [
    ...card.fields.map((f) => f.key),
    ...(card.id === 'upi' ? ['qrImage'] : []),
  ];
  keys.forEach((k) => {
    next[k] = d[k] != null ? String(d[k]).trim() : '';
  });
  return next;
}

function payloadForCard(card, form) {
  const payload = {};
  card.fields.forEach((f) => {
    payload[f.key] = (form[f.key] || '').trim();
  });
  if (card.id === 'upi') {
    payload.qrImage = (form.qrImage || '').trim();
  }
  return payload;
}

const SevaDonationAdmin = () => {
  const [forms, setForms] = useState(() =>
    Object.fromEntries(CARDS.map((c) => [c.id, { ...c.defaults }]))
  );
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [qrTab, setQrTab] = useState('url');
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState('');
  const fileInputRef = useRef(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  useEffect(() => {
    setLoading(true);
    homeApi
      .getAll()
      .then((res) => {
        if (res.success && res.data) {
          const d = res.data;
          setForms(() => {
            const next = {};
            CARDS.forEach((c) => {
              next[c.id] = normalizeCard(d[c.key], c);
            });
            return next;
          });
        } else if (res.message) {
          setError(res.message);
        }
      })
      .catch(() => setError('Failed to load seva donation details'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => () => {
    if (filePreview) URL.revokeObjectURL(filePreview);
  }, [filePreview]);

  const setField = (id, field, value) => {
    setForms((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const clearQrImage = () => {
    setSelectedFile(null);
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFilePreview('');
    setField('upi', 'qrImage', '');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (filePreview) URL.revokeObjectURL(filePreview);
    setSelectedFile(file);
    setFilePreview(URL.createObjectURL(file));
    setField('upi', 'qrImage', '');
  };

  const saveCard = async (card) => {
    let form = forms[card.id] || card.defaults;
    setSaving((prev) => ({ ...prev, [card.id]: true }));
    setError('');
    try {
      if (card.id === 'upi' && qrTab === 'file' && selectedFile) {
        const fd = new FormData();
        fd.append('image', selectedFile);
        const up = await homeApi.uploadImage(fd);
        if (!up.success || !up.data?.url) {
          throw new Error(up.message || 'Upload failed');
        }
        form = { ...form, qrImage: up.data.url };
        setForms((prev) => ({ ...prev, upi: { ...prev.upi, qrImage: up.data.url } }));
        setSelectedFile(null);
        if (filePreview) URL.revokeObjectURL(filePreview);
        setFilePreview('');
        setQrTab('url');
      }
      await homeApi.updateKey(card.key, payloadForCard(card, form));
      showToast(`${card.title} saved`);
    } catch {
      setError(`Failed to save ${card.title}`);
    } finally {
      setSaving((prev) => ({ ...prev, [card.id]: false }));
    }
  };

  const qrPreviewSrc = filePreview || (forms.upi?.qrImage || '').trim();

  const qrTabBtn = (tab, icon, label) => (
    <button
      type="button"
      onClick={() => setQrTab(tab)}
      className={`flex-1 flex items-center justify-center gap-2 py-2.5 transition-colors ${
        qrTab === tab ? 'admin-chip-active' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
      }`}
    >
      <i className={`fas ${icon}`}></i>
      {label}
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <i className="fas fa-spinner fa-spin text-orange-500 text-2xl"></i>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-gray-800 text-white px-5 py-3 rounded-2xl shadow-2xl text-sm font-medium">
          {toast}
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800">Seva Donation</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          UPI and bank details on the Donate page — each card saves separately
        </p>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <i className="fas fa-exclamation-circle mr-2"></i>{error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {CARDS.map((card) => {
          const form = forms[card.id] || card.defaults;
          const isSaving = !!saving[card.id];
          return (
            <div
              key={card.id}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 md:p-6 flex flex-col"
            >
              <h3 className="font-bold text-gray-800 mb-1 flex items-center gap-2">
                <i className={`${card.icon} ${card.iconColor}`}></i>
                {card.title}
              </h3>
              <p className="text-sm text-gray-500 mb-5">{card.hint}</p>

              <div className="space-y-4 flex-1">
                {card.fields.map((f) => (
                  <AdminFieldInput
                    key={f.key}
                    label={f.label}
                    value={form[f.key] || ''}
                    onChange={(e) => setField(card.id, f.key, e.target.value)}
                    onClear={() => setField(card.id, f.key, '')}
                    clearable
                    placeholder={f.placeholder}
                  />
                ))}

                {card.id === 'upi' && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      QR Code Image
                    </label>
                    <p className="text-xs text-gray-400 mb-2">
                      Optional. If empty, Donate page auto-generates QR from UPI ID.
                    </p>
                    <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm font-medium mb-3">
                      {qrTabBtn('url', 'fa-link', 'URL')}
                      {qrTabBtn('file', 'fa-upload', 'Upload')}
                    </div>

                    {qrTab === 'url' && (
                      <AdminUrlField
                        value={form.qrImage || ''}
                        onChange={(e) => {
                          setSelectedFile(null);
                          if (filePreview) URL.revokeObjectURL(filePreview);
                          setFilePreview('');
                          setField('upi', 'qrImage', e.target.value);
                        }}
                        onClear={clearQrImage}
                        placeholder="https://... or /path/to/qr.png"
                      />
                    )}

                    {qrTab === 'file' && (
                      <div>
                        <div
                          onClick={() => fileInputRef.current && fileInputRef.current.click()}
                          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-orange-400 transition-colors"
                        >
                          {selectedFile ? (
                            <p className="text-sm text-green-600 font-medium">
                              <i className="fas fa-check-circle mr-2"></i>{selectedFile.name}
                            </p>
                          ) : (
                            <>
                              <i className="fas fa-cloud-upload-alt text-gray-400 text-2xl mb-2"></i>
                              <p className="text-sm text-gray-500">Click to select QR image</p>
                              <p className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP up to 10 MB</p>
                            </>
                          )}
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={handleFileSelect}
                        />
                      </div>
                    )}

                    {qrPreviewSrc ? (
                      <div className="mt-4 mx-auto w-40 h-40 rounded-xl overflow-hidden border border-orange-100 bg-white p-2">
                        <img
                          src={qrPreviewSrc}
                          alt="UPI QR preview"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={() => saveCard(card)}
                  disabled={isSaving}
                  className="admin-home-save-btn disabled:opacity-60"
                >
                  {isSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SevaDonationAdmin;
